import { X, Check, RotateCcw, AlertTriangle } from 'lucide-react';

export interface RecomputeSummaryRow {
  label: string;
  before: string;
  after: string;
  changed: boolean;
}

interface RecomputePricingReviewDialogProps {
  isOpen: boolean;
  rows: RecomputeSummaryRow[];
  applying?: boolean;
  onAccept: () => void;
  onReject: () => void;
}

/**
 * Review modal shown after "Recompute Pricing" completes. Surfaces a before → after
 * summary of the deterministically recomputed pricing so the user can accept (write it
 * into the proposal) or reject (discard, leave the proposal untouched).
 */
export function RecomputePricingReviewDialog({
  isOpen,
  rows,
  applying = false,
  onAccept,
  onReject,
}: RecomputePricingReviewDialogProps) {
  if (!isOpen) return null;

  const changedCount = rows.filter(r => r.changed).length;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="w-full max-w-2xl bg-bw-surface dark:bg-gray-800 rounded-xl shadow-xl border border-bw-border dark:border-gray-700 overflow-hidden">
        {/* Header */}
        <div className="flex items-start justify-between px-5 py-4 border-b border-bw-border dark:border-gray-700">
          <div>
            <h3 className="text-base font-semibold text-bw-ink dark:text-white">Review Recomputed Pricing</h3>
            <p className="text-xs text-bw-muted dark:text-gray-400 mt-0.5">
              {changedCount > 0
                ? `${changedCount} field${changedCount === 1 ? '' : 's'} changed. Accept to apply to the proposal, or reject to keep it as is.`
                : 'No material changes detected. You can reject to leave the proposal unchanged.'}
            </p>
          </div>
          <button
            onClick={onReject}
            disabled={applying}
            className="text-bw-muted hover:text-bw-ink dark:hover:text-white disabled:opacity-50"
            aria-label="Close"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Summary table */}
        <div className="px-5 py-4 max-h-[55vh] overflow-y-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wide text-bw-muted dark:text-gray-400">
                <th className="py-2 pr-3 font-medium">Field</th>
                <th className="py-2 px-3 font-medium">Before</th>
                <th className="py-2 pl-3 font-medium">After</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => (
                <tr
                  key={i}
                  className={`border-t border-bw-border/60 dark:border-gray-700 ${r.changed ? 'bg-bw-primary-tint/40 dark:bg-blue-900/20' : ''}`}
                >
                  <td className="py-2 pr-3 font-medium text-bw-ink dark:text-gray-200 align-top">
                    {r.changed && <span className="inline-block w-1.5 h-1.5 rounded-full bg-bw-primary mr-1.5 align-middle" />}
                    {r.label}
                  </td>
                  <td className="py-2 px-3 text-bw-muted dark:text-gray-400 align-top break-words">{r.before || '—'}</td>
                  <td className={`py-2 pl-3 align-top break-words ${r.changed ? 'text-bw-ink dark:text-white font-medium' : 'text-bw-muted dark:text-gray-400'}`}>
                    {r.after || '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          <div className="flex items-start gap-2 mt-4 text-xs text-bw-muted dark:text-gray-400">
            <AlertTriangle className="h-3.5 w-3.5 mt-0.5 flex-shrink-0" />
            <span>
              Recompute re-runs only the deterministic pricing &amp; compliance engine against your stored
              grounded data — it does not regenerate narrative sections and uses no AI credits beyond a
              one-time solicitation re-read if needed.
            </span>
          </div>
        </div>

        {/* Actions */}
        <div className="flex items-center justify-end gap-2 px-5 py-4 border-t border-bw-border dark:border-gray-700 bg-bw-surface-subtle dark:bg-gray-900/40">
          <button
            onClick={onReject}
            disabled={applying}
            className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-bw-ink dark:text-gray-200 bg-bw-surface dark:bg-gray-700 border border-bw-border dark:border-gray-600 rounded-lg hover:bg-bw-border/40 transition-colors disabled:opacity-50"
          >
            <RotateCcw className="h-4 w-4" />
            Reject
          </button>
          <button
            onClick={onAccept}
            disabled={applying}
            className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-white bg-bw-primary hover:bg-bw-primary/90 rounded-lg transition-colors disabled:opacity-50"
          >
            <Check className="h-4 w-4" />
            {applying ? 'Applying…' : 'Accept changes'}
          </button>
        </div>
      </div>
    </div>
  );
}
